import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { CargoTempCategory } from '../../types/ViewModel/CargoTempLog/CargoInTankItem';
import { CargoTemperatureConfig } from '../../types/ViewModel/CargoTempLog/CargoTemperatureConfig';
import { CargoTemperatureLogEntry } from '../../types/ViewModel/CargoTempLog/CargoTemperatureLog';
import { CargoTemperatureLogDate } from '../../types/ViewModel/CargoTempLog/CargoTemperatureLogDate';
import { NoonReportSectionData } from '../../types/ViewModel/NoonReportSectionData';
import { Tank } from '../../types/ViewModel/Vessel/Tank';
import sortUtils from '../../utils/sortUtils';
import { CargoInfo } from 'src/types/ViewModel/CargoTempLog/CargoInfo';

export const MAX_OXYGEN_PER_CENT_VALUE = 22;
export const MAX_OXYGEN_PPM_VALUE = 220000;
export const MAX_TEMPERATURE_VALUE = 100;
export const MAX_PRESSURE_VALUE = 601;

export enum SortColumnEnum {
    CargoNr,
    TradeName,
    Tank,
    Category,
}

export enum tabEnum {
    reporting = 1,
    config = 2,
    export = 3,
}

export const inputTypes = ['temp', 'o2', 'pressure', 'comment'];

export type TankDetails = {
    tank: Tank;
    cargoLogs: CargoTemperatureLogEntry[][];
    cargoNumber: string;
    tradeName: string;
    category: CargoTempCategory;
};

export type CargoTemperatureControlVM = {
    cargoTanks: TankDetails[];
    sectionData?: NoonReportSectionData;
    notValidFieldsArray?: string[];
    hasInvalidFields?: boolean;
    isInitialized?: boolean;
};

export const getComparison = (sortDescending: boolean, stringA: string, stringB: string, tankA: string, tankB: string): number => {
    const { compareTankNames } = sortUtils();
    if (stringA > stringB) return sortDescending ? -1 : 1;
    if (stringA < stringB) return sortDescending ? 1 : -1;
    return compareTankNames(tankA, tankB, sortDescending);
};

export const validateCargoTempLog = (
    cargoTempLogDate: CargoTemperatureLogDate,
    cargoTempLog: CargoTemperatureLogEntry[],
    configs: CargoTemperatureConfig[]
): boolean => {
    if (!cargoTempLogDate || !configs || !cargoTempLog) return false;

    return configs.every((conf) => {
        const tankIds = cargoTempLogDate.cargoInTankList.filter((cargoInTank) => cargoInTank.cargoNumber === conf.cargoNumber).map((c) => c.tankId);
        return tankIds.every((tankId) => {
            return conf.logTimes.every((logTime) => {
                const onBoardItem = cargoTempLog.find(
                    (logItem) =>
                        logItem.cargoNumber === conf.cargoNumber &&
                        logItem.tankId === tankId &&
                        logItem.cargoTemperatureFieldId === 1 &&
                        formatLogTime(logItem.loggedTimeLocalTime) === logTime
                );
                if (onBoardItem && !onBoardItem.isOnBoard) return true;
                return conf.inputFields.every((input) => {
                    const logEntry = cargoTempLog.find(
                        (logItem) =>
                            logItem.cargoNumber === conf.cargoNumber &&
                            logItem.tankId === tankId &&
                            logItem.cargoTemperatureFieldId === input.cargoTemperatureInputFieldId &&
                            formatLogTime(logItem.loggedTimeLocalTime) === logTime
                    );
                    return !!logEntry?.value;
                });
            });
        });
    });
};

export const getCategoryName = (category: CargoTempCategory | undefined): string | null => {
    switch (category) {
        case 1:
            return 'Inhibited';
        case 2:
            return 'Heat sensitive';
        case 3:
            return 'Heated / Cooled';
        case 4:
            return 'Non-heat';
        default:
            return null;
    }
};

export const formatLogTime = (date: Date): string => {
    return ('0' + date.getHours()).slice(-2) + ':' + ('0' + date.getMinutes()).slice(-2);
};

export const CreateCargoDataForExcelTemperatureReport = (cargoNumbers: string[], activeMasterPlan: StowagePlan): CargoInfo[] => {
    const cargoData: CargoInfo[] = [];
    cargoNumbers.forEach((cargoNumber) => {
        const voyCargoNr = cargoNumber.split('-');
        const voyageNumber = voyCargoNr[0];
        if (voyCargoNr.length < 2) return; // skip if cargo number format is invalid
        // try to parse the cargo sequence number, default to -1 if not found
        const cargoSeqNumber = parseInt(voyCargoNr[1]) || -1;
        const cargo = activeMasterPlan.cargoes.find((c) => c.voyageNumber === voyageNumber && c.cargoNumber === cargoSeqNumber);
        const cargoOperations = activeMasterPlan.cargoOperations.filter(
            (co) => co.cargo.voyageNumber === voyageNumber && co.cargo.cargoNumber === cargoSeqNumber
        );
        const stowages = activeMasterPlan.stowageItems.filter((s) => cargoOperations.some((co) => co.cargoOperationGuid === s.cargoOperationGuid));
        if (stowages.length === 0) return;
        if (cargo) {
            // deduplicate tanks list
            const tanks = Array.from(new Set(stowages.map((s) => s.tankId)));
            cargoData.push({
                cargoNumber: cargoNumber,
                loadingPortName: cargo.loadingPortNames[0]?.name || '',
                unloadingPortName: cargo.unloadingPortNames[0]?.name || '',
                tanks: tanks,
                adjacentTempControlRequired: cargo.product?.maxAdjacentTemperature != null,
            });
        }
    });

    return cargoData;
};
